import { User } from '../types/user';
import { Shipping, ShippingUpdate, CalculatorRequest, CalculatorResponse } from '../types/shipping';
import { FileUploadData } from '../types/updates';
import { getToken } from './auth';
import { handleAuthError } from '../utils/authErrorHandler';
import { cleanUploadFilename, FileNamingContext } from '../utils/fileDownload';

// Base URL for API calls
const API_URL = '/.netlify/functions';

// Generic fetch function
export const fetchApi = async (endpoint: string, options: any = {}) => {
  try {
    const token = getToken();
    const url = `${API_URL}${endpoint}`;
    
    // Log the request for email endpoints
    if (endpoint.includes('send-email')) {
      console.log('🌐 fetchApi: Making request to:', url);
      console.log('🔑 fetchApi: Token present:', !!token);
      console.log('📦 fetchApi: Request options:', {
        method: options.method || 'GET',
        headers: options.headers,
        bodyLength: options.body?.length || 0
      });
    }
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      // A request that is too big, or a function that crashed, comes back from Netlify
      // with an empty or non-JSON body. Reading that as JSON is what showed "Unexpected
      // end of JSON input", so fall back to a plain message instead.
      const text = await response.text().catch(() => '');
      let error: any = {};
      try {
        error = text ? JSON.parse(text) : {};
      } catch {
        error = {};
      }
      if (!error.message) {
        error.message = error.error || (response.status === 413
          ? 'That upload is too large. Please use a smaller file.'
          : `The server could not finish this (error ${response.status}). Please try again.`);
      }
      
      // Log email endpoint errors
      if (endpoint.includes('send-email')) {
        console.error('❌ fetchApi: Email request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: error
        });
      }
      
      const errorObj = new Error(error.message || 'Request failed');
      // Add status code to error object for auth error detection
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    const result = await response.json();
    
    // Log successful email responses
    if (endpoint.includes('send-email')) {
      console.log('✅ fetchApi: Email request successful:', result);
    }
    
    return result;
  } catch (error: any) {
    console.error('API error:', error);
    // Handle authentication errors automatically
    await handleAuthError(error);
    throw error;
  }
};

// Authentication
export const login = async (email: string, password: string) => {
  try {
    const response = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const { token, user } = await response.json();
    
    // Store token in localStorage
    localStorage.setItem('lima_auth_token', token);
    
    // Store user in localStorage
    const userToStore = {
      _id: user._id || '',
      uname: user.email,
      role: user.role,
      active: true,
      clients: user.clients || [],
      clientName: user.clientName || null
    };
    localStorage.setItem('lima_user', JSON.stringify(userToStore));
    
    return { token, user: userToStore };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const register = async (email: string, password: string, fullName?: string) => {
  try {
    const response = await fetch(`${API_URL}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, fullName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    const { token, user } = await response.json();

    localStorage.setItem('lima_auth_token', token);

    const userToStore = {
      _id: user._id || '',
      uname: user.email,
      role: user.role,
      active: true,
      clients: user.clients || [],
      clientName: user.clientName || null
    };
    localStorage.setItem('lima_user', JSON.stringify(userToStore));

    return { token, user: userToStore };
  } catch (error) {
    console.error('Register error:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const response = await fetch(`${API_URL}/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Logout failed');
    }

    return response.json();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

// Clear browser data function for when user is not logged in
export const clearBrowserData = () => {
  localStorage.removeItem('lima_auth_token');
  localStorage.removeItem('lima_user');
  sessionStorage.clear();
  // Clear any cached data
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
    });
  }
};

// User operations
export const getUser = async (token: string) => {
  try {
    const response = await fetch(`${API_URL}/user`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get user failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get user error:', error);
    await handleAuthError(error);
    throw error;
  }
};

export const getUsers = async (token: string) => {
  try {
    const response = await fetch(`${API_URL}/users`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get users failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get users error:', error);
    await handleAuthError(error);
    throw error;
  }
};

export const createUser = async (user: Partial<User>, token: string) => {
  try {
    const response = await fetch(`${API_URL}/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Create user failed');
    }

    return response.json();
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

export const resendCredentials = async (userId: string, newPassword?: string) => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}/users/resend-credentials`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ userId, newPassword }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to resend credentials');
    }

    return data;
  } catch (error) {
    console.error('Resend credentials error:', error);
    throw error;
  }
};

export const updateUser = async (userEmail: string, user: Partial<User>, token: string) => {
  try {
    const response = await fetch(`${API_URL}/user/${encodeURIComponent(userEmail)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Update user failed');
    }

    return response.json();
  } catch (error) {
    console.error('Update user error:', error);
    throw error;
  }
};

/** Update the current user's own profile (clientName). */
export const updateOwnProfile = async (data: { clientName: string }, token: string) => {
  const response = await fetch(`${API_URL}/users`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.message || 'Update profile failed');
  }
  return response.json();
};

// Shipping operations
export const getShippings = async (token: string, filter?: string) => {
  try {
    const url = filter 
      ? `${API_URL}/shippings?filter=${encodeURIComponent(filter)}` 
      : `${API_URL}/shippings`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get shippings failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get shippings error:', error);
    await handleAuthError(error);
    throw error;
  }
};

// Get shipments by client email for autocomplete
export const getShipmentsByClientEmail = async (clientEmail: string, token: string) => {
  try {
    const response = await fetch(`${API_URL}/shippings?client=${encodeURIComponent(clientEmail)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get shipments by client failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get shipments by client error:', error);
    throw error;
  }
};

export const createShipping = async (shipping: Partial<Shipping>, token: string) => {
  try {
    // Generate custom SID from VIN if provided
    if (shipping.vin && !shipping.customSID) {
      shipping.customSID = shipping.vin.slice(-4); // Last 4 digits of VIN
    }

    const response = await fetch(`${API_URL}/shipping`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(shipping),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Create shipping failed');
    }

    return response.json();
  } catch (error) {
    console.error('Create shipping error:', error);
    throw error;
  }
};

// Shipping updates
export const getShippingUpdates = async (shippingId: string, token: string) => {
  try {
    const response = await fetch(`${API_URL}/shipping-updates/${encodeURIComponent(shippingId)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get shipping updates failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get shipping updates error:', error);
    throw error;
  }
};

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = error => reject(error);
  });
};

export const createShippingUpdate = async (
  shippingId: string, 
  update: Partial<ShippingUpdate>, 
  token: string,
  files?: FileUploadData[]
) => {
  try {
    // Validate required parameters
    if (!shippingId) {
      throw new Error('Shipping ID is required');
    }
    if (!update) {
      throw new Error('Update data is required');
    }

    const filesData = [];
    if (files && files.length > 0) {
      const validFiles = files.filter(f => f.file);
      const namingCtx: FileNamingContext = { shipmentId: shippingId, label: (update as any).title, totalFiles: validFiles.length };
      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        if (!fileData.file) {
          console.warn('Skipping invalid file');
          continue;
        }
        
        const base64Content = await fileToBase64(fileData.file);
        filesData.push({
          fileName: cleanUploadFilename(fileData.file.name, { ...namingCtx, fileIndex: i + 1 }),
          fileContent: base64Content,
          contentType: fileData.file.type,
          folder: fileData.folder || 'updates',
          updateId: fileData.updateId,
          shipmentId: fileData.shipmentId
        });
      }
    }

    const payload = {
      ...update,
      files: filesData
    };

    const response = await fetch(`${API_URL}/shipping-update/${encodeURIComponent(shippingId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorMessage = 'Create shipping update failed';
      try {
        const error = await response.json();
        errorMessage = error.message || errorMessage;
      } catch (parseError) {
        // If response isn't JSON, use status text
        errorMessage = `${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // If this was a new update and we got the real ObjectID back, 
    // we should update any file references to use the real ObjectID
    if (result.update && result.update._id) {
      console.log('Update created with ObjectID:', result.update._id);
    }

    return result;
  } catch (error) {
    console.error('Create shipping update error:', error);
    throw error;
  }
};

export const updateShippingUpdate = async (
  updateId: string,
  update: Partial<ShippingUpdate>,
  token: string,
  files?: FileUploadData[]
) => {
  try {
    // Validate required parameters
    if (!updateId) {
      throw new Error('Update ID is required');
    }
    if (!update) {
      throw new Error('Update data is required');
    }

    const filesData = [];
    if (files && files.length > 0) {
      const validFiles = files.filter(f => f.file);
      const sid = validFiles[0]?.shipmentId || (update as any).sid;
      const namingCtx: FileNamingContext = { shipmentId: sid, label: (update as any).title, totalFiles: validFiles.length };
      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        if (!fileData.file) {
          console.warn('Skipping invalid file');
          continue;
        }
        
        const base64Content = await fileToBase64(fileData.file);
        filesData.push({
          fileName: cleanUploadFilename(fileData.file.name, { ...namingCtx, fileIndex: i + 1 }),
          fileContent: base64Content,
          contentType: fileData.file.type,
          folder: fileData.folder || 'updates',
          updateId: fileData.updateId,
          shipmentId: fileData.shipmentId
        });
      }
    }

    const payload = {
      ...update,
      files: filesData
    };

    const response = await fetch(`${API_URL}/shipping-update/${encodeURIComponent(updateId)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorMessage = 'Update shipping update failed';
      try {
        const error = await response.json();
        errorMessage = error.message || errorMessage;
      } catch (parseError) {
        // If response isn't JSON, use status text
        errorMessage = `${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    console.error('Update shipping update error:', error);
    throw error;
  }
};

// Upload files to shipment folder for quick access
export const uploadShipmentFiles = async (
  shipmentId: string,
  files: File[],
  token: string,
  type?: 'onboarding'
) => {
  try {
    const filesData = [];
    const namingCtx: FileNamingContext = { shipmentId, label: type === 'onboarding' ? 'Onboarding' : undefined, totalFiles: files.length };
    for (let i = 0; i < files.length; i++) {
      const base64Content = await fileToBase64(files[i]);
      filesData.push({
        fileName: cleanUploadFilename(files[i].name, { ...namingCtx, fileIndex: i + 1 }),
        fileContent: base64Content,
        contentType: files[i].type
      });
    }

    const typeParam = type ? `&type=${type}` : '';
    const response = await fetch(`${API_URL}/documents?shippingId=${shipmentId}${typeParam}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ files: filesData }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Upload shipment files failed');
    }

    return response.json();
  } catch (error) {
    console.error('Upload shipment files error:', error);
    throw error;
  }
};

// Get files by folder structure
export const getFilesByFolder = async (shipmentId: string, folder: string, token: string) => {
  try {
    const response = await fetch(
      `${API_URL}/files/${encodeURIComponent(shipmentId)}?folder=${encodeURIComponent(folder)}`,
      {
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return []; // Return empty array for missing endpoints
      }
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || 'Get files by folder failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get files by folder error:', error);
    return []; // Return empty array instead of throwing
  }
};

export const sendNotification = async (
  shipmentIds: string[],
  subject: string,
  message: string,
  latitude: number,
  longitude: number,
  token: string,
  attachments?: FileUploadData[]
) => {
  try {
    // Convert attachments to base64 if any
    const attachmentsData = [];
    if (attachments && attachments.length > 0) {
      for (const fileData of attachments) {
        if (!fileData.file) continue;
        
        const base64Content = await fileToBase64(fileData.file);
        attachmentsData.push({
          fileName: cleanUploadFilename(fileData.file.name),
          fileContent: base64Content,
          contentType: fileData.file.type
        });
      }
    }

    const payload = {
      shipmentIds,
      subject,
      message,
      latitude,
      longitude,
      attachments: attachmentsData
    };

    const response = await fetch(`${API_URL}/send-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Send notification failed' }));
      throw new Error(error.message || 'Send notification failed');
    }

    return response.json();
  } catch (error) {
    console.error('Send notification error:', error);
    throw error;
  }
};

export const getDocuments = async (shippingId: string, token: string, type?: 'onboarding') => {
  try {
    const typeParam = type ? `&type=${type}` : '';
    const response = await fetch(`${API_URL}/documents?shippingId=${encodeURIComponent(shippingId)}${typeParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get documents failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get documents error:', error);
    throw error;
  }
};

export const getClientOnboardingDocuments = async (shipmentId: string, token: string) => {
  return getDocuments(shipmentId, token, 'onboarding');
};

/**
 * Upload a designated key document via server-side S3 upload.
 *
 * `docType` is one of the three built-in slots (registration, billOfSale,
 * identification) or a custom slug; pass `label` for a custom slot so every
 * page that lists key documents shows the name the user chose.
 */
export const uploadKeyDocument = async (
  shipmentId: string,
  docType: string,
  file: File,
  token: string,
  label?: string,
) => {
  const docTypeLabels: Record<string, string> = {
    registration: 'Registration',
    billOfSale: 'BillOfSale',
    identification: 'ID',
  };
  const fileName = cleanUploadFilename(file.name, {
    shipmentId,
    label: docTypeLabels[docType] || docType,
    fileIndex: 1,
    totalFiles: 1,
  });
  const fileContent = await fileToBase64(file);
  const response = await fetch(
    `${API_URL}/shipping-key-docs?shipmentId=${encodeURIComponent(shipmentId)}&docType=${encodeURIComponent(docType)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName,
        fileContent,
        contentType: file.type || 'application/octet-stream',
        ...(label ? { label } : {}),
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload key document failed' }));
    throw new Error(error.message || 'Upload key document failed');
  }

  return response.json();
};

/** Update shipment onboarding (client details, pickup, delivery, agreement) - for client portal */
export const updateShipmentOnboarding = async (
  shipmentId: string,
  data: {
    pickupAddress?: string;
    pickupContact?: string;
    deliveryAddress?: string;
    deliveryContact?: string;
    agreementSignedAt?: boolean | string;
    clientName?: string;
    clientPhone?: string;
    clientEmail?: string;
    origin?: string;
    destination?: string;
  },
  token: string
) => {
  try {
    const payload: Record<string, string | boolean> = {};
    if (data.pickupAddress !== undefined) payload.pickupAddress = data.pickupAddress;
    if (data.pickupContact !== undefined) payload.pickupContact = data.pickupContact;
    if (data.deliveryAddress !== undefined) payload.deliveryAddress = data.deliveryAddress;
    if (data.deliveryContact !== undefined) payload.deliveryContact = data.deliveryContact;
    if (data.agreementSignedAt !== undefined) payload.agreementSignedAt = data.agreementSignedAt;
    if (data.clientName !== undefined) payload.clientName = data.clientName;
    if (data.clientPhone !== undefined) payload.clientPhone = data.clientPhone;
    if (data.clientEmail !== undefined) payload.clientEmail = data.clientEmail;
    if (data.origin !== undefined) payload.origin = data.origin;
    if (data.destination !== undefined) payload.destination = data.destination;

    const response = await fetch(`${API_URL}/shipment-onboarding/${encodeURIComponent(shipmentId)}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Update onboarding failed');
    }

    return response.json();
  } catch (error) {
    console.error('Update shipment onboarding error:', error);
    throw error;
  }
};

// Calculator API functions
export const calculateShippingCost = async (request: CalculatorRequest): Promise<CalculatorResponse> => {
  try {
    const response = await fetch('/.netlify/functions/calculate-shipping', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to calculate shipping cost');
    }

    return response.json();
  } catch (error) {
    console.error('Calculate shipping cost error:', error);
    throw error;
  }
};

// Geocoding function removed - now using GPS service with LIMA Automotive API

export const sendEmail = async (emailData: {
  emailType: 'update' | 'shipment' | 'custom';
  recipients: string[];
  subject: string;
  message: string;
  shipmentId?: string;
  updateId?: string;
  includeAttachments?: boolean;
}) => {
  try {
    const response = await fetch(`${API_URL}/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      },
      body: JSON.stringify(emailData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Send email failed');
    }

    return response.json();
  } catch (error) {
    console.error('Send email error:', error);
    throw error;
  }
};

export const getEmailTemplates = async () => {
  try {
    const response = await fetch(`${API_URL}/send-email?action=templates`, {
      headers: {
        Authorization: `Bearer ${getToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get email templates failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get email templates error:', error);
    throw error;
  }
};

export const getAvailableRecipients = async () => {
  try {
    const response = await fetch(`${API_URL}/send-email?action=recipients`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get recipients failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get recipients error:', error);
    throw error;
  }
};

// Email management functions
export const getEmails = async (params?: {
  page?: number;
  limit?: number;
  emailType?: string;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `${API_URL}/emails${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get emails failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get emails error:', error);
    throw error;
  }
};

export const getRecentEmails = async () => {
  try {
    const response = await fetch(`${API_URL}/emails?action=recent`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get recent emails failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get recent emails error:', error);
    throw error;
  }
};

export const getEmailById = async (emailId: string) => {
  try {
    const response = await fetch(`${API_URL}/emails/${emailId}`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get email failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get email error:', error);
    throw error;
  }
};

export const saveEmail = async (emailData: {
  emailType: 'update' | 'shipment' | 'custom';
  recipients: string[];
  subject: string;
  message: string;
  shipmentId?: string;
  updateId?: string;
  includeAttachments?: boolean;
  status?: string;
}) => {
  try {
    const response = await fetch(`${API_URL}/emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(emailData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Save email failed');
    }

    return response.json();
  } catch (error) {
    console.error('Save email error:', error);
    throw error;
  }
};

export const updateEmailStatus = async (emailId: string, status: string, emailResult?: any) => {
  try {
    const response = await fetch(`${API_URL}/emails/${emailId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ status, emailResult }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Update email status failed');
    }

    return response.json();
  } catch (error) {
    console.error('Update email status error:', error);
    throw error;
  }
};

export const deleteEmail = async (emailId: string) => {
  try {
    const response = await fetch(`${API_URL}/emails/${emailId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Delete email failed');
    }

    return response.json();
  } catch (error) {
    console.error('Delete email error:', error);
    throw error;
  }
};

export const getEmailStats = async () => {
  try {
    const response = await fetch(`${API_URL}/emails?action=stats`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get email stats failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get email stats error:', error);
    throw error;
  }
};

// User-specific operations (for client/agent users)
export const getUserShippings = async (token: string, filter?: string) => {
  try {
    const url = filter 
      ? `${API_URL}/user-shippings?filter=${encodeURIComponent(filter)}` 
      : `${API_URL}/user-shippings`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get user shipments failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get user shipments error:', error);
    await handleAuthError(error);
    throw error;
  }
};

export const getUserUpdates = async (token: string, filter?: string) => {
  try {
    const url = filter 
      ? `${API_URL}/user-updates?filter=${encodeURIComponent(filter)}` 
      : `${API_URL}/user-updates`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get user updates failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get user updates error:', error);
    await handleAuthError(error);
    throw error;
  }
};

export const getShipmentPreview = async (token: string, shipmentId: string) => {
  try {
    const response = await fetch(`${API_URL}/shipment-preview/${shipmentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get shipment preview failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get shipment preview error:', error);
    await handleAuthError(error);
    throw error;
  }
};

export const getUserFiles = async (token: string, filter?: string) => {
  try {
    const url = filter 
      ? `${API_URL}/user-files?filter=${encodeURIComponent(filter)}` 
      : `${API_URL}/user-files`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      const errorObj = new Error(error.message || 'Get user files failed');
      (errorObj as any).status = response.status;
      throw errorObj;
    }

    return response.json();
  } catch (error: any) {
    console.error('Get user files error:', error);
    await handleAuthError(error);
    throw error;
  }
};

// Booking Request Documents API
export const getBookingRequestDocuments = async (requestId: string, token: string) => {
  try {
    const response = await fetch(`${API_URL}/booking-request-documents?requestId=${encodeURIComponent(requestId)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Get booking request documents failed');
    }

    return response.json();
  } catch (error) {
    console.error('Get booking request documents error:', error);
    throw error;
  }
};

export const uploadBookingRequestDocuments = async (
  requestId: string,
  files: Array<{ fileName: string; fileContent: string; contentType: string }>,
  token: string
) => {
  try {
    const response = await fetch(`${API_URL}/booking-request-documents?requestId=${encodeURIComponent(requestId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ files })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload booking request documents failed');
    }

    return response.json();
  } catch (error) {
    console.error('Upload booking request documents error:', error);
    throw error;
  }
};

export const changePassword = async (currentPassword: string, newPassword: string) => {
  try {
    const response = await fetch(`${API_URL}/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to change password');
    }

    return response.json();
  } catch (error) {
    console.error('Change password error:', error);
    throw error;
  }
};

export const deleteBookingRequestDocument = async (requestId: string, key: string, token: string) => {
  try {
    const response = await fetch(
      `${API_URL}/booking-request-documents?requestId=${encodeURIComponent(requestId)}&key=${encodeURIComponent(key)}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Delete booking request document failed');
    }

    return response.json();
  } catch (error) {
    console.error('Delete booking request document error:', error);
    throw error;
  }
};